import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import './CategorySlider.css';

const CategorySlider = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

 const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/categories/`);
      const data = await response.json();
      if (data.success) setCategories(data.data);
      else setError('Failed to fetch categories');
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    const path = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    const baseUrl = process.env.REACT_APP_BASE_URL.split('/api')[0];
    return `${baseUrl}/${path}`;
  };

  // ✅ FIXED: Pass category._id as the URL parameter and category.name as state
 const handleCategoryClick = (category) => {
    // Option 1: If products store category._id as a string
    navigate(`/category/${category._id}`, {
      state: { 
        categoryName: category.name,
        categoryId: category._id 
      }
    });
    
    // Option 2: If products store category.name as a string
    // Uncomment this and comment out the above if products use category names
    // navigate(`/category/${encodeURIComponent(category.name)}`, {
    //   state: { 
    //     categoryName: category.name,
    //     categoryId: category._id 
    //   }
    // });
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="category-slider-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-slider-container">
        <div className="error-container">
          <ShoppingBag />
          <p>{error}</p>
          <button onClick={fetchCategories}>Try Again</button>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="empty-state">
        <ShoppingBag />
        <h3>No Categories Found</h3>
        <p>Categories will appear here once they are added.</p>
      </div>
    );
  }

  return (
    <div className="category-slider-container">
      <div className="category-slider-wrapper">
        <div className="category-header">
          <h2>
            Shop By <span className="highlight">Categories</span>
          </h2>
        </div>

        <Swiper
          spaceBetween={16}
          slidesPerView={2}
          grabCursor={true}
          breakpoints={{
            480: { slidesPerView: 2.5, spaceBetween: 10 },
            576: { slidesPerView: 3, spaceBetween: 12 },
            768: { slidesPerView: 4, spaceBetween: 14 },
            992: { slidesPerView: 5, spaceBetween: 16 },
            1200: { slidesPerView: 7, spaceBetween: 24 },
          }}
        >
          {categories.map((category) => (
            <SwiperSlide key={category._id}>
              <div
                className="category-card"
                onClick={() => handleCategoryClick(category)}
                style={{ cursor: 'pointer' }}
              >
                <div className="category-image-container">
                  <img
                    src={getImageUrl(category.imageUrl) || '/api/placeholder/64/64'}
                    alt={category.name}
                    className="category-image"
                    onError={(e) => { e.target.src = '/api/placeholder/64/64'; }}
                  />
                </div>
                <div className="category-info">
                  <h3 className="category-name">{category.name}</h3>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default CategorySlider;